import { extractCaseTitle, stripHtmlTags } from "../../utilities/caseContent";
import React, { useEffect, useMemo, useState } from "react";
import cn from "classnames";
import moment from "moment";
import { StarIcon as StarSolidIcon } from "@heroicons/react/24/solid";
import { Case as CaseType } from "../../db/schema";
import {
  CASE_DRAG_MIME,
  useCaseCategories,
} from "../../context/CaseCategoriesContext";

interface CaseProps extends CaseType {
  selected?: boolean;
  handleSelectedCase: (caseId: string) => void;
  currentUserId?: string;
  isRecordsUser?: boolean;
  userName?: string; // Name of the case owner
  categoryIds?: string[]; // category ids this case belongs to (for the user)
  isFavorite?: boolean;
  draggable?: boolean; // disabled in the Trash view
}

function stylePlaceholders(text: string): string {
  // Wrap {{placeholder}} text with compact styling for sidebar previews
  // Hide the brackets using replace-text-bracket class
  return text.replace(
    /\{\{([^}]+)\}\}/g,
    '<span class="replace-text-bracket">{{</span><span class="replace-text-compact">$1</span><span class="replace-text-bracket">}}</span>'
  );
}

function Case({
  id,
  content,
  createdAt,
  updatedAt,
  status,
  handleSelectedCase,
  selected,
  userId, // This should be the case owner's ID
  currentUserId,
  isRecordsUser,
  userName,
  categoryIds,
  isFavorite,
  draggable = true,
}: CaseProps) {
  const { categories, toggleFavorite, setDraggingCase } = useCaseCategories();
  // Optimistic favorite state for snappy star toggling.
  const [fav, setFav] = useState(!!isFavorite);
  useEffect(() => setFav(!!isFavorite), [isFavorite]);

  // Resolve the case's category ids to "#name" tags (skip deleted categories).
  const catNames = useMemo(() => {
    if (!categoryIds || categoryIds.length === 0) return [];
    return categoryIds
      .map((cid) => categories.find((c) => c.id === cid))
      .filter((c): c is (typeof categories)[number] => !!c)
      .map((c) => c.name);
  }, [categoryIds, categories]);

  const handleStar = (e: React.MouseEvent) => {
    e.stopPropagation();
    const next = !fav;
    setFav(next);
    toggleFavorite(id, next);
  };

  const handleDragStart = (e: React.DragEvent) => {
    e.dataTransfer.setData(CASE_DRAG_MIME, id);
    e.dataTransfer.effectAllowed = "copyMove";

    // Drag a compact document icon instead of the full case snapshot. The
    // element must be in the DOM when setDragImage runs, so render it off-
    // screen and remove it on the next tick.
    const ghost = document.createElement("div");
    ghost.style.cssText =
      "position:absolute;top:-1000px;left:-1000px;padding:8px;background:oklch(var(--b1));border:1px solid oklch(var(--b3));border-radius:8px;box-shadow:0 1px 3px rgba(0,0,0,0.15)";
    ghost.innerHTML =
      '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24" stroke="oklch(var(--bc) / 0.7)" stroke-width="1.5"><path stroke-linecap="round" stroke-linejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z"/></svg>';
    document.body.appendChild(ghost);
    e.dataTransfer.setDragImage(ghost, 20, 20);
    setTimeout(() => ghost.remove(), 0);

    setDraggingCase(true);
  };

  const htmlContent = useMemo(
    () => (content as { data: string })?.data || "",
    [content]
  );
  const extractedTitle = useMemo(
    () => stylePlaceholders(extractCaseTitle(htmlContent)),
    [htmlContent]
  );
  const strippedContent = useMemo(
    () =>
      stylePlaceholders(
        stripHtmlTags(htmlContent).trim() || "No additional text"
      ),
    [htmlContent]
  );

  // Check if this case belongs to someone else (for records users)
  const isOtherUserCase = isRecordsUser && userId !== currentUserId;

  // Extract last name from full name
  const getLastName = (fullName: string | undefined): string => {
    if (!fullName) return "";
    const nameParts = fullName.trim().split(" ");
    return nameParts.length > 1 ? nameParts[nameParts.length - 1] : fullName;
  };

  const displayLastName = userName ? getLastName(userName) : null;

  return (
    <div
      data-tour="case-list-item"
      draggable={draggable}
      onDragStart={handleDragStart}
      onDragEnd={() => setDraggingCase(false)}
      className={cn(
        "group p-5 cursor-pointer hover:bg-base-200 transition duration-100 border-b-2 relative",
        selected
          ? "bg-gray-100 dark:bg-[#2a323c] rounded-lg hover:bg-gray-100 dark:hover:bg-[#2a323c] border-transparent"
          : "border-b-2 border-base-200 dark:border-base-300"
      )}
      onClick={() => {
        handleSelectedCase(id);
      }}
    >
      {status === "processing" && (
        <div className="absolute inset-0 bg-base-100/80 flex items-center justify-center">
          <div className="w-6 h-6 border-2 border-base-300 border-t-blue-500 rounded-full animate-spin"></div>
        </div>
      )}
      {/* Favorite star — only shown on already-favorited cases (no hover
          reveal); favoriting is done via the editor toolbar or drag-to-
          Favorites. */}
      {fav && (
        <button
          onClick={handleStar}
          title="Remove from Favorites"
          className="absolute top-3 right-3 p-1 rounded-full hover:bg-base-300 z-10"
        >
          <StarSolidIcon className="size-4 text-yellow-500" />
        </button>
      )}
      <div className="grid text-sm select-none">
        <h3
          className="font-semibold text-base-content border-b-base-200 line-clamp-2 2xl:line-clamp-1 text-xs 2xl:text-sm pr-6"
          dangerouslySetInnerHTML={{ __html: extractedTitle }}
        />
        <p
          className="text-black-300 dark:text-base-content/70 text-xs line-clamp-3 2xl:line-clamp-2 2xl:text-sm"
          dangerouslySetInnerHTML={{ __html: strippedContent }}
        />
        <div className="flex items-center justify-between mt-1 2xl:mt-2">
          <div className="flex items-center gap-2 min-w-0">
            <p className="text-base-content/40 text-xs 2xl:text-sm flex-shrink-0">
              {moment(updatedAt).format("MMM D, YYYY")}
            </p>
            {catNames.length > 0 && (
              <div className="flex items-center gap-1 min-w-0">
                {catNames.slice(0, 2).map((name, i) => (
                  <span
                    key={i}
                    className="text-xs text-base-content/40 truncate max-w-[90px]"
                  >
                    #{name}
                  </span>
                ))}
                {catNames.length > 2 && (
                  <span className="text-xs text-base-content/40 flex-shrink-0">
                    +{catNames.length - 2}
                  </span>
                )}
              </div>
            )}
          </div>
          {displayLastName && isOtherUserCase && (
            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900/60 text-blue-800 dark:text-blue-300">
              {displayLastName}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

export default React.memo(Case);
